import BaseHelper from './BaseHelper'
import * as COLORS from '../utils/colorUtils'
import { extent as d3_extent } from 'd3-array'
import { scaleOrdinal as d3_scaleOrdinal, scaleSequential as d3_scaleSequential } from 'd3-scale'
import { interpolateRgbBasis as d3_interpolateRgbBasis } from 'd3-interpolate'
import {} from 'd3-transition'
import { addPatterns, getPatternId } from '../utils/defsUtils'
import { isDarkMode } from '../utils/configUtils'

export const DEFAULT_DURATION = 1000
export const DEFAULT_DELAY_STEP = 80 / 1000

export default class ItemsHelper extends BaseHelper {
  static configName = 'ItemsHelper'

  static defaultConfig = {
    index: 101,
    className: {
      type: String,
      default: 'item',
    },
    /**
     * {-EN-} Colors to be assigned to each item. See [colorScales](#/developer/PropDetails#colorScale)
     * {-ES-} Colores a asignar a cada elemento. Ver [colorScales](#/developer/PropDetails#colorScale)
     */
    colorScale: {
      type: [String, Function, Array],
      default: 'categorical',
      options: ['categorical', 'sequential2', 'sequential3', 'single'],
      exposed: true,
    },
    /**
     * {-EN-} Dictionary to "manually" assign a color to each item. See [colorScales](#/developer/PropDetails#colorScale)
     * {-ES-} Diccionario para asignar "manualmente" un color a cada elemento. Ver [colorScales](#/developer/PropDetails#colorScale)
     */
    colorDict: {
      type: [Object, Array, null],
      default: null,
      exposed: true,
    },
    patternDict: {
      type: [Object, Array, null],
      default: null,
      exposed: true,
    },
    colorMode: {
      type: [String, Array],
      default: 'fill',
      options: ['fill', 'stroke'],
    },
    colorKey: {
      type: [String, Function],
      default: 'id',
    },
    /**
     * {-EN-} Enable/disable transition between datasets. Set to `true` for a `1000ms` transition. Or specify the `Number` of miliseconds.
     * {-ES-} Activar/desactivar la transición entre conjuntos de datos. Ajustado a `true` para `1000ms` de transición. O especifique el `Number` de milisegundos.
     */
    transition: {
      type: [Boolean, Number, String],
      default: true,
      exposed: true,
    },
  }
  static init() {
    this.__itemsNames = []
  }
  static _getCategories = function (data) {
    return Object.keys(
      data.reduce((res, d) => {
        let id = this._colorKey(d)
        if (id) res[id] = {}
        return res
      }, {})
    )
  }
  static _getScheme = function (schemeId, data) {
    if (schemeId === 'PRIMARY_CAT_SCHEME') {
      let categories = this._getCategories(data)
      if (categories.length < 9) schemeId = 'PRIMARY_CAT_SCHEME_LOW'
      else if (categories.length < 16) schemeId = 'PRIMARY_CAT_SCHEME_MID'
      else schemeId = 'PRIMARY_CAT_SCHEME_HIGH'
    }
    return COLORS[
      schemeId + (isDarkMode(this.config, this._parent.accessibilityOptions) ? '_DARK' : '')
    ]
  }
  static _setCachedColorScale = function (colorScale, colorDict, i) {
    let customColors =
      colorDict &&
      Object.keys(colorDict).reduce((res, key) => {
        res[key] = colorDict[key]?.color || colorDict[key]
        return res
      }, {})

    // CHAPUZILLA para que:
    // cuando estamos en "multiData" con algun "dataSorting" con scale de "categroies" y sin "customColors" => mantenga los colores asignados previamente!
    if (!customColors) {
      if (
        this._parent.data.isMulti &&
        this.config.dataSorting &&
        this.config.dataSorting !== 'none'
      ) {
        if (this.cachedColorScale && this.cachedColorScale.domain) {
          customColors = this.cachedColorScale.domain().reduce((ob, k) => {
            ob[k] = this.cachedColorScale(k)
            return ob
          }, {})
        }
      }
    }
    //CHAPUZA bars and lines... :(
    let data
    let allData = this.allData || this.data
    if (!Array.isArray(allData)) {
      if (i === 1) data = allData.bars //WTF!! porque estaba "al reves"?!?!?!?
      else data = allData.lines
    } else data = allData

    // Si hay custom colors pero no rellenan todo, rellenamos con los "que se pueda" de la primary_cat_scheme
    if (
      colorScale === 'categorical' &&
      customColors &&
      data.filter(d => !customColors[this._colorKey(d)]).length > 0
    ) {
      let unusedColors = [...this._getScheme('PRIMARY_CAT_SCHEME', data)]
      unusedColors = unusedColors.filter(d => Object.values(customColors).indexOf(d) < 0)
      data.forEach((d, i) => {
        if (!customColors[this._colorKey(d)]) {
          customColors[this._colorKey(d)] = unusedColors[0]
          unusedColors.shift()
        }
      })
    }

    let result
    if (colorScale === 'single') {
      result = () =>
        customColors && customColors['all'] ? customColors['all'] : this._getScheme('PRIMARY_COLOR')
      result.isSinlge = true
    } else if (colorScale === 'single2') {
      result = () =>
        customColors && customColors['all']
          ? customColors['all']
          : this._getScheme('SECONDARY_COLOR')
      result.isSinlge = true
    } else if (colorScale === 'categorical') {
      if (customColors)
        result = d3_scaleOrdinal()
          .domain(Object.keys(customColors))
          .range(Object.values(customColors))
      else
        result = d3_scaleOrdinal(this._getScheme('PRIMARY_CAT_SCHEME', data)).domain(
          this._getCategories(data)
        ) //added this DOMAIN to preent d3scale from using internally cached colors
    } else if (colorScale === 'categorical2') {
      if (customColors)
        result = d3_scaleOrdinal()
          .domain(Object.keys(customColors))
          .range(Object.values(customColors))
      //Solo sirve para "allData.bars / allData.lines", pero creo qeu categorical2 solo sirve "para esto"
      else
        result = d3_scaleOrdinal(
          this._getScheme('PRIMARY_CAT_SCHEME', data).slice(allData.bars?.length || 0)
        ).domain(this._getCategories(data)) //added this DOMAIN to preent d3scale from using internally cached colors
    } else if (colorScale === 'sequential2') {
      if (customColors)
        result = d3_scaleSequential().interpolator(
          d3_interpolateRgbBasis(Object.values(customColors))
        )
      else
        result = d3_scaleSequential().interpolator(
          d3_interpolateRgbBasis(this._getScheme('PRIMARY_SEQ2_SCHEME'))
        )
    } else if (colorScale === 'sequential3') {
      if (customColors)
        result = d3_scaleSequential().interpolator(
          d3_interpolateRgbBasis(Object.values(customColors))
        )
      else
        result = d3_scaleSequential().interpolator(
          d3_interpolateRgbBasis(this._getScheme('PRIMARY_SEQ3_SCHEME'))
        )
    } else if (colorScale === 'variation') {
      if (customColors)
        result = d3_scaleOrdinal()
          .domain(Object.keys(customColors))
          .range(Object.values(customColors))
      else {
        result = d3_scaleOrdinal(this._getScheme('PRIMARY_VAR_SCHEME', data)).domain([
          'initial value',
          'positive',
          'negative',
          'total',
        ])
      }
    } else {
      result = colorScale
    }
    return result
  }

  static update() {
    if (Array.isArray(this.config.ItemsHelper.colorScale)) {
      this.cachedColorScale = []
      this.config.ItemsHelper.colorScale.forEach((c, i) => {
        this.cachedColorScale[i] = this._setCachedColorScale(
          this.config.ItemsHelper.colorScale[i],
          this.config.ItemsHelper.colorDict[i],
          i
        )
      })
    } else {
      this.cachedColorScale = this._setCachedColorScale(
        this.config.ItemsHelper.colorScale,
        this.config.ItemsHelper.colorDict
      )
    }
  }

  static _colorKey = function (d) {
    if (typeof this.config.ItemsHelper.colorKey === 'function')
      return this.config.ItemsHelper.colorKey(d)
    else return d[this.config.ItemsHelper.colorKey]
  }

  static color = function (d, i) {
    if (typeof this.config.ItemsHelper.colorScale === 'function') {
      return this.cachedColorScale(d)
    } else {
      if (Array.isArray(this.cachedColorScale)) {
        return this.cachedColorScale[i](this._colorKey(d), i)
      } else {
        return this.cachedColorScale(this._colorKey(d))
      }
    }
  }

  static patternMask = function (d, i) {
    const currentPattern = this.config.ItemsHelper?.colorDict?.[d.id]?.pattern
    return currentPattern ? `url(#${currentPattern.id}-mask)` : undefined
  }

  static fill = function (d, i) {
    const color = this.color(d, i)
    let pattern
    if (this.config.ItemsHelper?.colorScale === 'variation' && d.colorType) {
      const variationKeys = {
        'initial value': 'initial value',
        positive: 'positive',
        negative: 'negative',
        total: 'total',
      }
      const variationKey = variationKeys[d.colorType]
      pattern = this.config.ItemsHelper?.colorDict?.[variationKey]?.pattern
    }
    if (!pattern) {
      if (
        (!Array.isArray(this.config.ItemsHelper?.colorScale) &&
          !['single', 'single2'].includes(this.config.ItemsHelper?.colorScale)) ||
        (Array.isArray(this.config.ItemsHelper?.colorScale) &&
          !['single', 'single2'].includes(this.config.ItemsHelper?.colorScale[1]))
      )
        pattern = Array.isArray(this.config.ItemsHelper?.colorDict)
          ? this.config.ItemsHelper?.colorDict[1]?.[this._colorKey(d)]?.pattern
          : this.config.ItemsHelper?.colorDict?.[this._colorKey(d)]?.pattern
    }
    let patternId
    if (pattern) patternId = getPatternId(pattern.id, color)
    return patternId ? `url(#${patternId})` : color
  }

  static strokeDasharray = function (d, i) {
    let dict = Array.isArray(this.config.ItemsHelper.colorDict)
      ? this.config.ItemsHelper.colorDict[i]
      : this.config.ItemsHelper.colorDict
    let mode = Array.isArray(this.config.ItemsHelper.colorMode)
      ? this.config.ItemsHelper.colorMode[i]
      : this.config.ItemsHelper.colorMode
    if (mode === 'stroke' && dict) {
      if (dict[this._colorKey(d)] && dict[this._colorKey(d)].stroke === 'dashed') return '5 3'
      else return 'none'
    } else return 'none'
  }

  static thickness = function (d, i) {
    let dict = Array.isArray(this.config.ItemsHelper.colorDict)
      ? this.config.ItemsHelper.colorDict[i]
      : this.config.ItemsHelper.colorDict
    let mode = Array.isArray(this.config.ItemsHelper.colorMode)
      ? this.config.ItemsHelper.colorMode[i]
      : this.config.ItemsHelper.colorMode
    if (mode === 'stroke' && dict) {
      if (dict[this._colorKey(d)] && dict[this._colorKey(d)].thickness === 'thick') return 6
      else if (dict[this._colorKey(d)] && dict[this._colorKey(d)].thickness === 'medium') return 3
      else return 1
    } else return 1
  }

  static _drawItems = function (data, rootSel, itemsName, className) {
    let updates = rootSel.selectAll(`.${className}`).data(data, d => d.id)
    let enters = updates.enter().append('g').attr('class', className)
    let exits = updates.exit()

    // lo hacemos en "diferido" para no pasar por los "getters/setters" del multichart
    this[itemsName] = updates
    this[itemsName + 'Enter'] = enters
    this[itemsName + 'Exit'] = exits

    let duration = 0
    if (this.config.ItemsHelper.transition) {
      duration =
        this.config.ItemsHelper.transition === true
          ? DEFAULT_DURATION
          : this.config.ItemsHelper.transition
    }
    if (window.fake_d3_duration) {
      duration = window.fake_d3_duration
    }

    this._transitionDuration = duration

    let delayStep = Math.min(duration / data.length, duration * DEFAULT_DELAY_STEP)
    // console.log("DELAY STEP",delayStep)
    if (this._zooming) {
      duration = 0
      delayStep = 0
    }
    this[itemsName + 'Trans'] = this[itemsName].transition().duration(duration)
    this[itemsName + 'EnterTrans'] = this[itemsName + 'Enter']
      .transition()
      .duration(duration)
      .delay((d, i) => i * delayStep)
    this[itemsName + 'ExitTrans'] = this[itemsName + 'Exit']
      .transition()
      .duration(duration)
      .remove()

    if (this.__itemsNames.indexOf(itemsName) < 0) this.__itemsNames.push(itemsName)
    // INTERESANTE pero muy loco....
    // Object.defineProperties(this, {
    //   [itemsName+'Trans'] :{
    //     get () {
    //       if(self[itemsName+'Trans_internal'].active) {
    //         return self[itemsName+'Trans_internal']
    //       }
    //       else {
    //         return self[itemsName]
    //       }
    //     }
    //   }
    // });
  }

  static getDuration = function () {
    return (
      window.fake_d3_duration ||
      (this.config.ItemsHelper.transition === true ? 1000 : this.config.ItemsHelper.transition)
    )
  }

  static beforeDraw() {
    // ESTO ESTABA "FATAL" porque no los Enter se quedavan y se repintavan cada scroll!?!?!
    // se arregla poniendo el if(this._zooming) en el _drawItems()
    // if(this._zooming) {
    //   this.__itemsNames.forEach((itemsName)=>{
    //     this[itemsName+'Trans'] = this[itemsName].transition().duration(0)
    //     this[itemsName+'EnterTrans'] = this[itemsName+'Enter'].transition().duration(0)
    //     this[itemsName+'ExitTrans'] = this[itemsName+'Exit'].transition().duration(0).remove()
    //   })
    //   return
    // }

    if (
      typeof this.config.ItemsHelper.colorScale == 'string' &&
      this.config.ItemsHelper.colorScale.indexOf('sequential') >= 0
    ) {
      this.cachedColorScale.domain(d3_extent(this.data.map(d => this._colorKey(d)))).nice()
    }
    let bindingData = this.ghostData || this.data

    if (!Array.isArray(this.data)) {
      //BASURA: esta todo hardcodeado para lines and bars... hay que hacerlo generico o limpiarlo

      // bindingData = {} (Bars and lines)
      // let types = Object.keys(bindingData)
      let types = ['bars', 'lines']

      //En caso de que use ghostData para lines and bars
      if (Array.isArray(bindingData)) {
        bindingData = {
          bars: [],
          lines: [],
        }
      }
      types.forEach(type => {
        if (type === 'bars') {
          //ahora la chapuza es "doble" porque el bars puede tener dimensions (stacked/grouped) o no (simple)
          if (bindingData.bars?.dimensions) {
            let className = `parent-${type}-${this.config.ItemsHelper.className}`
            this._drawItems(
              bindingData.bars || [],
              this.content || this.root,
              type + 'ParentItems',
              className
            )

            className = type + '-' + this.config.ItemsHelper.className
            this._drawItems(
              d => d.value,
              this.barsParentItems.merge(this.barsParentItemsEnter),
              type + 'Items',
              className
            )
          } else {
            let className = type + '-' + this.config.ItemsHelper.className
            this._drawItems(
              bindingData.bars[0]?.value || [],
              this.content || this.root,
              type + 'Items',
              className
            )
          }
        } else if (type === 'lines') {
          let className = `parent-${type}-${this.config.ItemsHelper.className}`
          this._drawItems(
            bindingData.lines || [],
            this.content || this.root,
            type + 'ParentItems',
            className
          )

          className = type + '-' + this.config.ItemsHelper.className
          this._drawItems(
            d => d.value,
            this.linesParentItems.merge(this.linesParentItemsEnter),
            type + 'Items',
            className
          )
        }
      })
    } else if (this.data[0] && this.data[0].dimensions !== undefined) {
      // bindingData = [dimensions:[]] (Parallel coords)
      let className = `parent-${this.config.ItemsHelper.className}`
      this._drawItems(bindingData, this.content || this.root, 'parentItems', className)

      className = this.config.ItemsHelper.className
      this._drawItems(
        d =>
          Object.keys(d.dimensions).map(dim => ({
            id: dim,
            value: d.dimensions[dim],
            parentId: d.id,
          })),
        this.parentItems.merge(this.parentItemsEnter),
        'items',
        className
      )
    } else if (this.data[0] && Array.isArray(this.data[0].value)) {
      // bindingData = [[]] (Lines and Stackets)
      let className = `parent-${this.config.ItemsHelper.className}`
      this._drawItems(bindingData, this.content || this.root, 'parentItems', className)

      className = this.config.ItemsHelper.className
      this._drawItems(
        d => d.value,
        this.parentItems.merge(this.parentItemsEnter),
        'items',
        className
      )
    } else {
      // bindingData = [] (Bars)
      this._drawItems(
        bindingData,
        this.content || this.root,
        'items',
        this.config.ItemsHelper.className
      )
    }
    if (this.helperListeners['afterItemsHelper']) this.helperListeners['afterItemsHelper']()
    if (
      this.config.ItemsHelper.colorDict &&
      (!Array.isArray(this.config.ItemsHelper.colorDict) || this.config.ItemsHelper.colorDict[1])
    )
      //Draw patterns
      addPatterns(
        this,
        Object.values(
          Array.isArray(this.config.ItemsHelper.colorDict)
            ? this.config.ItemsHelper.colorDict[1]
            : this.config.ItemsHelper.colorDict
        ).reduce((accumulator, current) => {
          if (!!current?.pattern) {
            accumulator.push(current)
          }
          return accumulator
        }, [])
      )
    else addPatterns(this, [])
  }
  static draw() {}
  static afterDraw() {
    if (this.constructor.name === 'PieChartChart')
      this._prevData = [...(this.ghostData || this.data)]
  }
}
